/* KANON `Programy v2 - D3 (0.8)` — the filter panel's four BODIES.
 *
 * The shell around them — the popup frame, the head, the tab strip, the body box and the footer —
 * moved to `shared/ui/filterPanel.module.css` on 17 September 2026, on its second consumer: the
 * exercise library draws the same panel because its own specimen transcribes this one verbatim.
 * Nothing about the shell changed in the move, and nothing here restates a shell property.
 *
 * What is left is this screen's own four tabs, which are not the other screen's:
 *   chips    gap 6.5; chip 29 tall, 0 10.5, radius 9, 10 / 700; on: --cat-wash over --cat-ink
 *   modes    gap 6.5; option 35 tall, 0 11, radius 9.5, 11 / 600; on: --accent-soft, --accent edge
 *   slider   gap 9.5, 2 above; figure 17.5 / 800 / --ls-display; unit 10.5 / 500; maks. 9 / 700
 *   rail     5 tall, radius 3, --bg-sunken, filled with --accent-grad; thumb 14.5, 2px --accent
 *   ticks    17.5 square, radius 5.5, 9 / 700; the chosen one --text over --on-ink
 *   clients  gap 4, max 233.5 tall and scrolls; option 37 tall, padding 5.5 7, radius 9
 *
 * **Every text leaf states its line-height.** `base.css` puts `--lh-relaxed` on `body` and the
 * specimen's body carries none, so an unstated leaf renders about 2px taller than the canon.
 *
 * Heights and widths are layout rather than design tokens (see stylelint.config.js); every colour,
 * space, radius and type value below is a token. */

/* Every checkbox and radio in the panel is off screen rather than `display: none`, which would take
 * it out of the accessibility tree and with it the whole keyboard contract. The label around it is
 * the control a trainer sees, so the ring is drawn there. `ScopeSwitch` states the same rule.
 *
 * Each label is `position: relative` so the input is positioned against ITS OWN control rather than
 * against whatever positioned ancestor happens to be up the tree — inside a portalled popup that is
 * the positioner, and the input lands somewhere the pointer cannot reach it. Measured: Playwright
 * refused a `check()` on the Siła chip with "element is outside of the viewport". */
.control {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  white-space: nowrap;
  clip-path: inset(50%);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6-5px);
}

.chip {
  position: relative;
  display: inline-flex;
  gap: var(--space-6-5px);
  align-items: center;
  height: var(--h-pl-tab-chip);
  padding: var(--space-0) var(--space-10-5px);
  font-size: var(--fs-pl-control);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-mark);
  cursor: pointer;
}

/* Chosen: the category's own wash behind its own ink, which is `planCategoryColour`'s two locals
 * and the same pair the create modal's chips wear. */
.chipOn {
  color: var(--cat-ink);
  background: var(--cat-wash);
  border-color: var(--cat-ink);
}

/* The mark is the category's raw hue while the chip is off and its ink once it is on — the canon's
 * own pair (`on ? ink(k) : CAT[k].c`), which is what keeps six unchosen chips legible as six
 * categories rather than as six grey pills. */
.chipIcon {
  display: block;
  flex: none;
  width: 13px;
  height: 13px;
  color: var(--cat-ink);
}

.chipCheck {
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: var(--cat-ink);
}

.chip:has(.control:focus-visible) {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

/* ── Tryb ──────────────────────────────────────────────────────────────────────────────────── */

.modes {
  display: flex;
  flex-direction: column;
  gap: var(--space-6-5px);
}

.mode {
  position: relative;
  display: flex;
  gap: var(--space-2);
  align-items: center;
  height: var(--h-pl-action);
  padding: var(--space-0) var(--space-11px);
  font-size: var(--fs-pl-option);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  background: transparent;
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-9-5px);
  cursor: pointer;
}

/* The one accent on this panel, and it marks a real state: which of three modes the list is showing.
 * A wash and a tinted edge, never a glow. */
.modeOn {
  color: var(--text);
  background: var(--accent-soft);
  border-color: var(--accent-border);
}

.modeIcon {
  display: block;
  flex: none;
  width: 13.5px;
  height: 13.5px;
  color: var(--text-2);
}

.modeCheck {
  font-size: var(--fs-micro-label);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: var(--accent-text);
}

.mode:has(.control:focus-visible) {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

/* ── Dni ───────────────────────────────────────────────────────────────────────────────────── */

.slider {
  display: flex;
  flex-direction: column;
  gap: var(--space-9-5px);
  padding: var(--space-2px) var(--space-0) var(--space-0);
}

.readout {
  display: inline-flex;
  gap: var(--space-5px);
  align-items: baseline;
  font-size: var(--fs-pl-readout);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

.readoutFigure {
  font-size: var(--fs-pl-figure);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-display);
  color: var(--text);
  font-variant-numeric: tabular-nums;
}

.readoutMax {
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-tag);
  color: var(--text-3);
  text-transform: uppercase;
}

/* The rail and its fill are ELEMENTS, and the native range lies transparently over them.
 *
 * A `::-webkit-slider-runnable-track` cannot carry the canon's fill: `--accent-grad` is a gradient
 * in the Flame themes and a flat ink in the mono one, and a `background-image` written against it
 * renders in one and not the other. Two elements take either value through one `background`
 * declaration, and the input keeps the keyboard path, the value announcement and the drag. */
.track {
  position: relative;
  display: block;
  height: var(--h-pl-track);
  background: var(--bg-sunken);
  border-radius: var(--radius-bar);
}

.trackFill {
  display: block;
  width: var(--pl-days);
  height: 100%;
  background: var(--accent-grad);
  border-radius: var(--radius-bar);
}

/* Over the rail, its own box the height of the handle so the hit area is the handle's rather than a
 * five-pixel line. Everything it draws itself is transparent; the thumb below is the one visible
 * part. */
.range {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: var(--h-pl-thumb);
  margin: var(--space-0);
  background: transparent;
  transform: translateY(-50%);
  cursor: pointer;
  appearance: none;
}

.range::-webkit-slider-runnable-track {
  height: var(--h-pl-thumb);
  background: transparent;
}

.range::-moz-range-track {
  height: var(--h-pl-thumb);
  background: transparent;
}

.range::-webkit-slider-thumb {
  width: var(--h-pl-thumb);
  height: var(--h-pl-thumb);
  background: var(--bg-card);
  border: var(--border-ring) solid var(--accent);
  border-radius: var(--r-pill);
  box-shadow: var(--shadow-pl-thumb);
  appearance: none;
}

.range::-moz-range-thumb {
  width: var(--h-pl-thumb);
  height: var(--h-pl-thumb);
  background: var(--bg-card);
  border: var(--border-ring) solid var(--accent);
  border-radius: var(--r-pill);
  box-shadow: var(--shadow-pl-thumb);
}

.range:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

.ticks {
  display: flex;
  justify-content: space-between;
}

.tick {
  display: grid;
  place-items: center;
  width: var(--h-pl-tick);
  height: var(--h-pl-tick);
  font-size: var(--fs-pl-micro);
  font-weight: var(--fw-bold);
  line-height: var(--lh-none);
  color: var(--text-3);
  background: transparent;
  border-radius: var(--radius-5-5px);
  font-variant-numeric: tabular-nums;
}

.tickOn {
  color: var(--on-ink);
  background: var(--text);
}

/* ── Klient ────────────────────────────────────────────────────────────────────────────────── */

/* Its own scroller, at the canon's height: nine clients would otherwise make the panel taller than
 * the pane behind it, and a popup that outgrows its own screen is the clip this repository's
 * fit-or-scroll rule exists to prevent. */
.clients {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  max-height: var(--h-pl-client-list);
  overflow-y: auto;
}

.client {
  position: relative;
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  height: var(--h-pl-client-opt);
  padding: var(--space-5-5px) var(--space-7px);
  background: transparent;
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-mark);
  cursor: pointer;
}

.clientOn {
  background: var(--bg-inset);
  border-color: var(--text);
}

/* NOT `shared/ui/AvatarChip`: that control is 32px and its header refuses a `className` that
 * restyles it, and the canon draws 22.5 here inside a 37px row. A third diameter with a skin of its
 * own, exactly as the inspector's strip has two of its own. */
.clientAvatar {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--h-pl-close);
  height: var(--h-pl-close);
  overflow: hidden;
  font-size: var(--fs-pl-eyebrow);
  font-weight: var(--fw-extrabold);
  font-style: normal;
  line-height: var(--lh-none);
  letter-spacing: var(--ls-nav);
  color: var(--text-2);
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-pill);
}

.clientBody {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-1px);
  min-width: 0;
}

.clientName {
  overflow: hidden;
  font-size: var(--fs-pl-control);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  letter-spacing: var(--ls-plan);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.clientSub {
  font-size: var(--fs-pl-sub);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-3);
  white-space: nowrap;
}

.clientCheck {
  display: grid;
  flex: none;
  place-items: center;
  width: 15px;
  height: 15px;
  font-size: var(--fs-pl-eyebrow);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  color: var(--on-ink);
  background: var(--text);
  border-radius: var(--r-pill);
}

.client:has(.control:focus-visible) {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}
